import pLimit from 'p-limit'
import { chunkArray } from '@/util/array-utils'
import { kUserFilesInitUploadMaxBatchSize } from '@/user_files/limits'
import { InitUploadFileResponse, InitUploadResponse } from '../schemas'
import { kUppyUploadMaxConcurrentUploads } from '../constants'
import { FileUploadState } from '../store/slice'

type UploadPipelineFile = Pick<FileUploadState, 'client'>

type InitUploadBatchInput = {
  clientTmpId: string
  filename: string
  folderId: string
}

type RunUppyUploadPipelineParams = {
  files: readonly UploadPipelineFile[]
  initUploadBatch: (files: InitUploadBatchInput[]) => Promise<InitUploadResponse>
  getBlob: (params: { uppyFileId: string; clientTmpId: string }) => Blob | null | undefined | Promise<Blob | null | undefined>
  uploadBlob: (params: { clientTmpId: string; uploadTarget: InitUploadFileResponse; blob: Blob; onProgress: (progress: number) => void }) => Promise<void>
  onFileInitializing: (clientTmpId: string) => void
  onFileInitialized: (params: { clientTmpId: string; serverFileId: string; storagePath: string }) => void
  onUploadStarted: (clientTmpId: string) => void
  onUploadProgress: (params: { clientTmpId: string; progress: number }) => void
  onUploadCompleted: (clientTmpId: string) => void
  onUploadFailed: (params: { clientTmpId: string; error: string }) => void
  onUploadBlobConsumed?: (params: { uppyFileId: string; clientTmpId: string }) => void
  initUploadBatchSize?: number
  maxConcurrentUploads?: number
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

export async function runUppyUploadPipeline(params: RunUppyUploadPipelineParams): Promise<void> {
  if (params.files.length === 0) return

  // Mark all files as initializing before any network calls.
  params.files.forEach((file) => {
    params.onFileInitializing(file.client.clientTmpId)
  })

  const uploadTargetMap = new Map<string, InitUploadFileResponse>()
  const initChunks = chunkArray(params.files, params.initUploadBatchSize ?? kUserFilesInitUploadMaxBatchSize)

  for (const chunk of initChunks) {
    try {
      const initResponse = await params.initUploadBatch(
        chunk.map((file) => ({
          clientTmpId: file.client.clientTmpId,
          filename: file.client.filename,
          folderId: file.client.folderId,
        }))
      )

      initResponse.files.forEach((item) => {
        uploadTargetMap.set(item.client_tmp_id, item)
      })

      chunk.forEach((file) => {
        const clientTmpId = file.client.clientTmpId
        const uploadTarget = uploadTargetMap.get(clientTmpId)

        if (!uploadTarget) {
          params.onUploadFailed({
            clientTmpId,
            error: 'Init-upload did not return a storage path for this file',
          })
          return
        }

        params.onFileInitialized({
          clientTmpId,
          serverFileId: uploadTarget.file_id,
          storagePath: uploadTarget.storage_path,
        })
        params.onUploadStarted(clientTmpId)
      })
    } catch (error) {
      const message = getErrorMessage(error, 'Init upload failed')
      chunk.forEach((file) => {
        params.onUploadFailed({
          clientTmpId: file.client.clientTmpId,
          error: message,
        })
      })
    }
  }

  const filesReadyForUpload = params.files.filter((file) => uploadTargetMap.has(file.client.clientTmpId))
  if (filesReadyForUpload.length === 0) return

  const limit = pLimit(params.maxConcurrentUploads ?? kUppyUploadMaxConcurrentUploads)
  const uploadPromises = filesReadyForUpload.map((file) =>
    limit(async () => {
      const clientTmpId = file.client.clientTmpId
      const uploadTarget = uploadTargetMap.get(clientTmpId)

      if (!uploadTarget) {
        params.onUploadFailed({
          clientTmpId,
          error: 'No storage path returned from server',
        })
        return
      }

      const blob = await params.getBlob({
        uppyFileId: file.client.uppyFileId,
        clientTmpId,
      })
      if (!blob) {
        params.onUploadFailed({
          clientTmpId,
          error: 'File data not found',
        })
        return
      }

      try {
        await params.uploadBlob({
          clientTmpId,
          uploadTarget,
          blob,
          onProgress: (progress) => {
            params.onUploadProgress({ clientTmpId, progress })
          },
        })

        params.onUploadCompleted(clientTmpId)
        params.onUploadBlobConsumed?.({
          uppyFileId: file.client.uppyFileId,
          clientTmpId,
        })
      } catch (error) {
        params.onUploadFailed({
          clientTmpId,
          error: getErrorMessage(error, 'Upload failed'),
        })
      }
    })
  )

  await Promise.all(uploadPromises)
}
